// src/features/business-functions/services/workspaceService.js
import APIService from '../../../Services/APIService';
import { Apis } from '../../../Services/app.constants';

/**
 * Workspace = Business Service (ProposalInfo / campaign).
 * Tables = Lead forms from Mongo via existing leads APIs.
 */
export const workspaceService = {
  /**
   * Create a new business service (initial proposal) under an account.
   * @param {string} accountId
   * @param {object} payload
   */
  async createInitialProposal(accountId, payload) {
    if (!accountId) throw new Error('Account ID is required');
    return APIService.post(
      `v0/ui/website/${encodeURIComponent(accountId)}/create-initial-proposal/`,
      payload
    );
  },

  /**
   * Fetch business service (proposal) metadata for workspace header.
   */
  async getWorkspaceDetail(accountId, workspaceId) {
    if (!workspaceId) throw new Error('Workspace ID is required');

    if (accountId) {
      const res = await APIService.get(
        `v0/ui/website/${encodeURIComponent(accountId)}/getAccountProposals/`
      );
      if (res?.status === 200 || res?.status === 201) {
        const proposals = Array.isArray(res.data) ? res.data : res.data?.list ?? [];
        const match = proposals.find(
          (item) => `${item.proposal_id}` === `${workspaceId}` || `${item.id}` === `${workspaceId}`
        );
        if (match) {
          return { status: res.status, data: match };
        }
      }
    }

    return APIService.get(`v0/ui/website/${encodeURIComponent(workspaceId)}/currentProposal/`);
  },

  async getWorkspaceMappings(workspaceId) {
    if (!workspaceId) throw new Error('Business service ID is required');
    const query = new URLSearchParams({ campaign_id: String(workspaceId) });
    return APIService.get(`v0/ui/proposal/create-campaign-mapping/?${query.toString()}`);
  },

  async createWorkspaceMapping(payload) {
    return APIService.post('v0/ui/proposal/create-campaign-mapping/', payload);
  },

  async updateWorkspaceMapping(payload) {
    return APIService.put('v0/ui/proposal/create-campaign-mapping/', payload);
  },

  async deleteWorkspaceMapping(mappingId) {
    if (!mappingId) throw new Error('Mapping ID is required');
    const query = new URLSearchParams({ _id: String(mappingId) });
    return APIService.remove(`v0/ui/proposal/create-campaign-mapping/?${query.toString()}`);
  },

  async getBusinessServiceOptions(params = {}) {
    const query = new URLSearchParams({
      to: String(params.to ?? 0),
      include_assigned_by: String(params.include_assigned_by ?? 0),
      fetch_all: String(params.fetch_all ?? 0),
      next_page: String(params.next_page ?? 1),
      search: params.search ?? '',
    });
    return APIService.get(`${Apis.Get_Campaign_Assignment}?${query.toString()}`);
  },

  /**
   * Fetch lead forms for a campaign — each form renders as a workspace table card.
   */
  async getWorkspaceTables(workspaceId) {
    if (!workspaceId) throw new Error('Workspace ID is required');
    return APIService.get(`v0/ui/leads/${encodeURIComponent(workspaceId)}/form`);
  },

  /**
   * Fetch a single lead form definition (field schema).
   */
  async getLeadFormById(formId) {
    if (!formId) throw new Error('Form ID is required');
    return APIService.get(
      `v0/ui/leads/${encodeURIComponent(formId)}/form_by_id/`
    );
  },

  /**
   * Fetch record count for a lead form (tables card metadata / toolbar).
   */
  async getLeadFormRecordCount(formId, workspaceId, params = {}) {
    if (!formId) throw new Error('Form ID is required');

    if (workspaceId) {
      const query = new URLSearchParams({
        campaign_id: String(workspaceId),
        leads_form_id: String(formId),
        include_unique_count: 'true',
      });
      if (params.status) {
        query.set('status', String(params.status));
      }
      if (params.parent_lead_id) {
        query.set('parent_lead_id', String(params.parent_lead_id));
      }

      const res = await APIService.get(`${Apis.countB2b2cLeads}?${query.toString()}`);
      if (res?.status === 200 || res?.status === 201) {
        return {
          status: res.status,
          data: {
            total_count: res.data?.total_count ?? 0,
            unique_count: res.data?.unique_count ?? null,
          },
        };
      }
    }

    return APIService.get(
      `v0/ui/leads/${encodeURIComponent(formId)}/entry_list/?page_number=1`
    );
  },

  /**
   * Fetch paginated records for a lead form table.
   * Uses the same B2B2C leads API as the main Leads module.
   */
  async getTableRecords(workspaceId, tableId, params = {}) {
    if (!workspaceId || !tableId) throw new Error('Workspace and table ID are required');

    const pageSize = Number(params.pageSize ?? params.limit ?? 10);
    const page = Number(params.page ?? 0);
    const offset = params.page !== undefined
      ? page * pageSize
      : (params.offset ?? 0);

    const query = new URLSearchParams({
      campaign_id: String(workspaceId),
      leads_form_id: String(tableId),
      limit: String(pageSize),
      offset: String(offset),
      search: params.search ?? '',
      start_date: params.start_date ?? '',
      end_date: params.end_date ?? '',
      status: params.status ?? '',
      lead_assigned_to: params.lead_assigned_to ?? '',
      is_pending: params.is_pending ?? '',
      include_count: params.include_count ?? 'false',
      include_unique_count: params.include_unique_count ?? 'false',
    });

    if (params.sort_by && params.sort_type !== undefined && params.sort_type !== '') {
      query.set('sort_by', String(params.sort_by));
      query.set('sort_type', String(params.sort_type));
    }

    if (params.data_filters) {
      query.set('data_filters', params.data_filters);
    }

    if (params.fields) {
      query.set('fields', params.fields);
    }
    if (params.parent_lead_id) {
      query.set('parent_lead_id', String(params.parent_lead_id));
    }

    return APIService.get(`${Apis.getB2b2cLeads}?${query.toString()}`);
  },
};
